import React, { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import CustomModal from './CustomModal';
import { Button } from './button';
import { RotateCw } from 'lucide-react';
import { getCroppedImageBlob } from '../../utils/cropImage';

const CropImageModal = ({
  isOpen,
  imageSrc,
  onCancel,
  onConfirm,
  title = 'Ajustar foto',
  outputSize = 512
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.7);
  const [minZoom, setMinZoom] = useState(0.2);
  const [maxZoom] = useState(4);
  const [cropPixels, setCropPixels] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTouched(false);
      setCrop({ x: 0, y: 0 });
      setZoom(0.7);
      setMinZoom(0.2);
      setRotation(0);
      setCropPixels(null);
    }
  }, [isOpen, imageSrc]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCropPixels(croppedAreaPixels);
  }, []);

  const handleCropChange = useCallback((location) => {
    setTouched(true);
    setCrop(location);
  }, []);

  const handleZoomChange = useCallback((value) => {
    setTouched(true);
    setZoom(value);
  }, []);

  const handleRotate = useCallback(() => {
    setTouched(true);
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!cropPixels) return;
    try {
      setSaving(true);
      const blob = await getCroppedImageBlob(imageSrc, cropPixels, outputSize, rotation);
      const file = new File([blob], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' });
      if (onConfirm) {
        onConfirm(file);
      }
    } catch (error) {
      console.error('Error recortando imagen:', error);
    } finally {
      setSaving(false);
    }
  }, [cropPixels, imageSrc, onConfirm, outputSize]);

  return (
    <CustomModal isOpen={isOpen} onClose={onCancel} title={title} size="md" guardOnClose isDirty={touched}>
      <div className="space-y-4">
        <div className="relative w-full bg-slate-100 rounded-lg overflow-hidden" style={{ height: 320 }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              onCropChange={handleCropChange}
              onZoomChange={handleZoomChange}
              onCropComplete={onCropComplete}
              objectFit="contain"
              minZoom={minZoom}
              maxZoom={maxZoom}
              onMediaLoaded={(mediaSize) => {
                if (!mediaSize?.width || !mediaSize?.height) {
                  setMinZoom(0.2);
                  setZoom((prev) => Math.max(prev, 0.2));
                  return;
                }
                const shortest = Math.min(mediaSize.width, mediaSize.height);
                const longest = Math.max(mediaSize.width, mediaSize.height);
                const safeMin = Math.max(0.2, Math.min(1, shortest / longest));
                setMinZoom(safeMin);
                setZoom((prev) => Math.max(prev, safeMin));
              }}
            />
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-slate-700">Zoom</label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRotate}
            >
              <RotateCw className="h-4 w-4 mr-2" />
              Girar
            </Button>
          </div>
          <input
            type="range"
            min={minZoom}
            max={maxZoom}
            step={0.05}
            value={zoom}
            onChange={(event) => handleZoomChange(parseFloat(event.target.value))}
            className="w-full"
          />
        </div>

        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={saving || !cropPixels}>
            {saving ? 'Procesando...' : 'Usar foto'}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default CropImageModal;
